import type { FC } from "react";
import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { isAxiosError } from "axios";

import { usePatchConsultation } from "@/api/consultations";
import Calendar from "@/assets/icons/calendar";
import {
  FieldDatepicker,
  FieldMultipleCheckbox,
  FieldSelect,
  FieldTextArea,
} from "@/components/form";
import GenericModal from "@/components/modals/generic-modal";
import { updateConsultationSchema } from "@/schemas/consultation";
import type { Consultation } from "@/types/api/consultation";
import { consultationStepsOptions } from "@/types/api/consultation";
import { resetForm, stopPropagation } from "@/utils/form";
import { mouthTypeOptions, sectorOptions } from "@/utils/treatment";

type PatchConsultationModalProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  consultation: Consultation;
  patientId: number;
};

const PatchConsultationModal: FC<PatchConsultationModalProps> = ({
  open,
  setOpen,
  consultation,
  patientId,
}) => {
  const { mutate } = usePatchConsultation(patientId, consultation.id);

  const { Field, handleSubmit, store, setFieldValue } = useForm({
    defaultValues: {
      startDate: new Date(consultation.startDate),
      endDate: new Date(consultation.endDate),
      consultationStep: consultation.consultationStep,
      comment: consultation.comment ?? null,
      toothbrushTypes: consultation.toothbrushTypes,
      toothbrushHairTypes: consultation.toothbrushHairTypes,
      toothbrushBrand: consultation.toothbrushBrand ?? null,
      brushesDimensions: consultation.brushesDimensions ?? null,
      brushesBrand: consultation.brushesBrand ?? null,
      sectors: consultation.sectors,
      mouthParts: consultation.mouthParts,
    },
    onSubmit: async ({ value, formApi }) => {
      const values = updateConsultationSchema.parse(value);
      if (values.toothbrushTypes === null || !values.toothbrushTypes) {
        values.toothbrushTypes = [];
      }
      if (values.toothbrushHairTypes === null || !values.toothbrushHairTypes) {
        values.toothbrushHairTypes = [];
      }
      if (values.sectors === null || !values.sectors) {
        values.sectors = [];
      }
      if (values.mouthParts === null || !values.mouthParts) {
        values.mouthParts = [];
      }
      mutate(values, {
        onSuccess: () => {
          setOpen(false);
          resetForm(values, formApi);
        },
        onError: (e) => {
          if (isAxiosError(e)) {
            if (e.code === "ERR_NETWORK")
              return {
                fields: {
                  comment: "Erreur réseau, merci de réessayer ultérieurement",
                },
              };
          }
        },
      });
    },
  });

  const startDateValue = useStore(store, (state) => state.values.startDate);
  const consultationStepValue = useStore(
    store,
    (state) => state.values.consultationStep
  );

  useEffect(() => {
    if (!startDateValue) return;
    const newDate = new Date(startDateValue);
    newDate.setHours(newDate.getHours() + 1);
    setFieldValue("endDate", newDate);
  }, [startDateValue]);

  return (
    <GenericModal
      title="Modifier une consultation"
      description="Complétez les informations de la consultation"
      submitLabel="Modifier"
      logo={<Calendar className="w-6 h-6" color="black" />}
      open={open}
      setOpen={setOpen}
      handleSubmit={handleSubmit}
    >
      <form
        onSubmit={(e) => stopPropagation(e, handleSubmit)}
        className="grid grid-flow-row grid-cols-1 gap-6 px-6 w-full"
      >
        <FieldDatepicker
          Field={Field}
          name="startDate"
          label="Date de consultation"
          schema={updateConsultationSchema}
        />
        <FieldSelect
          Field={Field}
          name="consultationStep"
          options={consultationStepsOptions}
          label="Motif"
          schema={updateConsultationSchema}
        />
        <FieldTextArea
          Field={Field}
          name="comment"
          placeholder="Commentaires"
          schema={updateConsultationSchema}
        />
        {consultationStepValue === "DEBRIDEMENT" ? (
          <FieldMultipleCheckbox
            Field={Field}
            options={sectorOptions}
            orientation="horizontal"
            name="sectors"
            schema={updateConsultationSchema}
          />
        ) : null}
        {consultationStepValue === "DEBRIDEMENT" ? (
          <FieldMultipleCheckbox
            Field={Field}
            options={mouthTypeOptions}
            orientation="horizontal"
            name="mouthParts"
            schema={updateConsultationSchema}
          />
        ) : null}
        <input type="submit" className="hidden" />
      </form>
    </GenericModal>
  );
};

export default PatchConsultationModal;
